import React from "react";
import { Box, Typography, Container, Grid } from "@mui/material";
import { styled, keyframes } from "@mui/material/styles";
import { motion } from "framer-motion";
import { Bot, MessageSquare, Link2 } from "lucide-react";

const pillars = [
  {
    icon: Bot,
    title: "AI-Powered Automation",
    description:
      "Personalized reports, auto-generated recommendations, and real-time insights to improve clinical efficiency.",
  },
  {
    icon: MessageSquare,
    title: "Omnichannel Communication",
    description:
      "WhatsApp, email, dashboard, and video-based reporting to keep patients continuously informed.",
  },
  {
    icon: Link2,
    title: "Seamless Lab Integrations",
    description:
      "FHIR-ready, LIS-integrated architecture supporting scalable digital transformation.",
  },
];

const SectionContainer = styled(Box)(({ theme }) => ({
  paddingTop: theme.spacing(12),
  paddingBottom: theme.spacing(12),
  backgroundColor: "#ffffff",
  [theme.breakpoints.down("md")]: {
    paddingTop: theme.spacing(8),
    paddingBottom: theme.spacing(8),
  },
}));

const GradientNavy = styled(Box)(({ theme }) => ({
  background: "linear-gradient(135deg, #022674 0%, #1C3D7A 100%)",
  borderRadius: "1rem",
  padding: theme.spacing(8),
  [theme.breakpoints.down("md")]: {
    padding: theme.spacing(6),
  },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: "2rem",
  fontWeight: 700,
  color: "#FFFFFF",
  textAlign: "center",
  marginBottom: theme.spacing(10),
  letterSpacing: "-0.01em",
  [theme.breakpoints.down("md")]: {
    fontSize: "1.75rem",
    marginBottom: theme.spacing(6),
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "1.5rem",
    marginBottom: theme.spacing(5),
  },
}));

const iconPulse = keyframes`
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
`;

const PillarCard = styled(motion.div)(({ theme }) => ({
  textAlign: "center",
  transition: "transform 0.3s ease",
  "&:hover": {
    transform: "translateY(-8px)",
  },
}));

const IconContainer = styled(Box)(({ theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: "4rem",
  height: "4rem",
  borderRadius: "1rem",
  backgroundColor: "rgba(255, 255, 255, 0.15)",
  marginBottom: theme.spacing(4),
  transition: "all 0.3s ease",
  "&:hover": {
    backgroundColor: "rgba(255, 255, 255, 0.25)",
    "& > *": {
      animation: `${iconPulse} 0.6s ease-in-out`,
    },
  },
}));

const PillarTitle = styled(Typography)(({ theme }) => ({
  fontSize: "1.25rem",
  fontWeight: 600,
  color: "#FFFFFF",
  marginBottom: theme.spacing(3),
}));

const PillarDescription = styled(Typography)(({ theme }) => ({
  fontSize: "1rem",
  color: "rgba(255, 255, 255, 0.85)",
  lineHeight: 1.75,
}));

const PillarsSection = () => {
  return (
    <SectionContainer>
      <Container>
        <GradientNavy>
          {/* Section Title */}
          <SectionTitle>
            The Pillars of Modern Patient Engagement
          </SectionTitle>

          {/* Pillars Grid */}
          <Grid container spacing={{ xs: 3, sm: 3, md: 4 }}>
            {pillars.map((pillar, index) => (
              <Grid item xs={12} md={4} key={pillar.title}>
                <PillarCard
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 0.6, delay: index * 0.2 }}
                >
                  <IconContainer>
                    <pillar.icon style={{ width: "2rem", height: "2rem", color: "#FFFFFF" }} />
                  </IconContainer>
                  <PillarTitle>{pillar.title}</PillarTitle>
                  <PillarDescription>{pillar.description}</PillarDescription>
                </PillarCard>
              </Grid>
            ))}
          </Grid>
        </GradientNavy>
      </Container>
    </SectionContainer>
  );
};

export default PillarsSection;

